import { useState, ButtonHTMLAttributes, useImperativeHandle, forwardRef } from 'react';
import { Button, Group, ButtonProps } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import ConfirmCloseDialog from './ConfirmCloseDialog';

interface FormActionButtonsProps {
  // Core functionality
  onSave?: () => void | Promise<void>;
  onDiscard: () => void;
  onClose: () => void;

  // State
  hasUnsavedChanges: boolean;
  isLoading?: boolean;
  isFormValid?: boolean;

  // Labels & customization
  saveLabel?: string;
  discardLabel?: string;

  // Additional button props
  saveButtonProps?: Partial<ButtonProps>;

  // Customization for confirm dialogs
  discardConfirmTitle?: string;
  discardConfirmMessage?: string;
  closeConfirmTitle?: string;
  closeConfirmMessage?: string;
}

export interface FormActionButtonsRef {
  handleGuardedClose: () => void;
}

export const FormActionButtons = forwardRef<
  FormActionButtonsRef,
  FormActionButtonsProps
>(({
  onSave,
  onDiscard,
  onClose,
  hasUnsavedChanges,
  isLoading = false,
  isFormValid = true,
  saveLabel,
  discardLabel,
  saveButtonProps = {},
  discardConfirmTitle,
  discardConfirmMessage,
  closeConfirmTitle,
  closeConfirmMessage,
}, ref) => {
  const { t } = useTranslation(['common']);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);

  // Discard handlers
  const handleDiscardClick = () => {
    setShowDiscardDialog(true);
  };

  const handleConfirmDiscard = () => {
    setShowDiscardDialog(false);
    onDiscard();
  };

  const handleCancelDiscard = () => {
    setShowDiscardDialog(false);
  };

  // Close handlers (exposed via ref)
  const handleGuardedClose = () => {
    if (hasUnsavedChanges) {
      setShowCloseDialog(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowCloseDialog(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowCloseDialog(false);
  };

  // Expose handleGuardedClose via ref
  useImperativeHandle(ref, () => ({
    handleGuardedClose,
  }));

  // Auto-detect submit type
  const submitType: ButtonHTMLAttributes<HTMLButtonElement>['type'] = onSave
    ? 'button'
    : 'submit';

  const handleSaveClick = onSave ? onSave : undefined;

  return (
    <>
      <Group justify="space-between" mt="md">
        {/* Discard Changes Button */}
        <Button
          variant="outline"
          color="yellow"
          onClick={handleDiscardClick}
          disabled={!hasUnsavedChanges || isLoading}
          loading={isLoading}
        >
          {discardLabel || t('discard.label', { defaultValue: 'Discard Changes' })}
        </Button>

        {/* Save/Submit Button */}
        <Button
          type={submitType}
          onClick={handleSaveClick}
          loading={isLoading}
          disabled={!isFormValid || isLoading}
          {...saveButtonProps}
        >
          {saveLabel || t('save', { defaultValue: 'Save' })}
        </Button>
      </Group>

      {/* Discard Confirmation Dialog */}
      <ConfirmCloseDialog
        opened={showDiscardDialog}
        onCancel={handleCancelDiscard}
        onConfirm={handleConfirmDiscard}
        title={
          discardConfirmTitle ||
          t('discard.confirmTitle', {
            defaultValue: 'Discard Changes',
          })
        }
        message={
          discardConfirmMessage ||
          t('discard.confirmMessage', {
            defaultValue:
              'Are you sure you want to discard all changes? This action cannot be undone.',
          })
        }
        confirmLabel={t('discard.confirmLabel', {
          defaultValue: 'Discard Changes',
        })}
        cancelLabel={t('discard.cancelLabel', { defaultValue: 'Cancel' })}
        confirmColor="yellow"
      />

      {/* Close Confirmation Dialog */}
      <ConfirmCloseDialog
        opened={showCloseDialog}
        onCancel={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={closeConfirmTitle}
        message={closeConfirmMessage}
      />
    </>
  );
});

FormActionButtons.displayName = 'FormActionButtons';

export default FormActionButtons;
